// Data source for the game-wins leaderboard.
// Live path: POST /dashboard/v1/get_leaderboard with ranking_criteria='game_wins'.
// Set Environment.LEADERBOARD_USE_MOCK = true to fall back to the local
// simulated engine for offline UI work (no backend required).

import ChessRestClient from '@/Api/ChessRestClient';
import Environment from '../../../environment';
import {mockLeaderboardEngine} from './mockData';
import {LeaderboardSnapshot, Period, TrendDir} from './types';

export const USE_MOCK: boolean =
  (Environment as {LEADERBOARD_USE_MOCK?: boolean})?.LEADERBOARD_USE_MOCK ===
  true;

interface ApiEntry {
  rank: number;
  userid: number;
  name: string;
  rating: number;
  avatar_url?: string;
  wins: number;
  trend?: TrendDir;
  trend_delta?: number | null;
}

interface ApiLeaderboardData {
  period: Period;
  total_players: number;
  leader_wins: number;
  entries: ApiEntry[];
  me: {
    userid: number;
    rank: number | null;
    wins: number;
    rating: number;
    name: string;
    avatar_url?: string;
    in_top: boolean;
    trend?: TrendDir;
    trend_delta?: number | null;
  };
}

function mapEnvelope(
  period: Period,
  payload: {success: boolean; message?: string; data?: ApiLeaderboardData},
): LeaderboardSnapshot {
  if (!payload?.success || !payload?.data) {
    throw new Error(payload?.message || 'leaderboard fetch failed');
  }
  const d = payload.data;
  return {
    period,
    entries: (d.entries || []).map(e => ({
      userid: e.userid,
      rank: e.rank,
      name: e.name,
      rating: e.rating,
      wins: e.wins,
      avatarUrl: e.avatar_url || undefined,
      you: e.userid === d.me?.userid,
      trend: e.trend,
      trendDelta: e.trend_delta,
    })),
    me: {
      rank: d.me?.rank ?? null,
      wins: d.me?.wins ?? 0,
      rating: d.me?.rating ?? 0,
      name: d.me?.name ?? '',
      avatarUrl: d.me?.avatar_url || undefined,
      inTop: !!d.me?.in_top,
      trend: d.me?.trend,
      trendDelta: d.me?.trend_delta,
    },
    totalPlayers: d.total_players ?? 0,
    leaderWins: d.leader_wins ?? 0,
  };
}

export async function fetchLeaderboard(
  period: Period,
  topN: number,
): Promise<LeaderboardSnapshot> {
  if (USE_MOCK) {
    return mockLeaderboardEngine.snapshot(period);
  }
  const response = await ChessRestClient.post('/dashboard/v1/get_leaderboard', {
    ranking_criteria: 'game_wins',
    duration: period,
    leaderboard_type: 'global',
    top_n: topN,
  });
  return mapEnvelope(period, response.data);
}
